<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { PdpEcommerceBlockProductInfoProps } from './PdpEcommerceBlockProductInfo.props'

const props = defineProps<PdpEcommerceBlockProductInfoProps>()
const emit = defineEmits<{
  'update-wishlist': []
  'complete-the-look': []
  'open-virtual-mirror': [eventDetail: string]
}>()

const url = useRequestURL()
const fullURL = url.href

const { wishlistProductCodes } = useWishlist()
const { openDialog } = useDialog()
const addedToWishlist = ref()
const { isShoppable } = useIsShoppable()

onMounted(() => {
  addedToWishlist.value = wishlistProductCodes.value[props.productCode]
})

watch(wishlistProductCodes, () => {
  addedToWishlist.value = wishlistProductCodes.value[props.productCode]
})
</script>

<template>
  <div class="lg:pb-lg lg:pt-3xl xl:pt-7xl flex flex-col">
    <div
      class="hidden overflow-hidden lg:-mr-[calc(var(--padding-x))] lg:mb-6 lg:block xl:mb-12"
    >
      <!-- Small gallery -->
      <PdpEcommerceBlockGalleryThumbnails :gallery="gallery" />
    </div>

    <div
      class="py-md flex h-full flex-col justify-between lg:gap-6 lg:py-0 xl:gap-16"
    >
      <div class="lg:gap-sm gap-lg xl:gap-lg flex flex-col">
        <div class="flex flex-col">
          <!-- Info & Details Section -->
          <PdpEcommerceBlockProgressiveLabel
            :label="$ts('pdp.productDetails.infoAndDetails')"
            @click="
              () =>
                openDialog(
                  SIDEBAR_IDS.pdpEcommerceblockProductInfoAndDetailsSideSlide
                )
            "
          />
          <PdpEcommerceBlockProgressiveLabel
            v-if="productCare.items?.length"
            :label="$ts('pdp.productCare')"
            @click="
              () =>
                openDialog(SIDEBAR_IDS.pdpEcommerceBlockProductCareSideSlide)
            "
          />
          <PdpEcommerceBlockProgressiveLabel
            v-if="!!productFocus?.items?.length"
            :label="$ts('pdp.productFocus')"
            @click="
              () =>
                openDialog(SIDEBAR_IDS.pdpEcommerceBlockProductFocusSideSlide)
            "
          />
          <PdpEcommerceBlockProgressiveLabel
            v-if="hasVirtualMirror"
            :label="$ts('pdp.virtualMirror.label')"
            @click="emit('open-virtual-mirror', 'product_info_entrypoints')"
          />
          <PdpProductFocusTrackingProvider
            v-for="(deliveryKey, index) of editorialProductFocus"
            :key="deliveryKey"
            :position="index + 1"
          >
            <EditorialModulesWrapper :delivery-key="deliveryKey" />
          </PdpProductFocusTrackingProvider>
        </div>

        <div class="gap-md flex items-center">
          <button
            v-if="isShoppable"
            :data-testid="
              addedToWishlist ? 'added-to-wishlist' : 'not-in-wishlist'
            "
            :aria-label="
              !!wishlistProductCodes[productCode]
                ? $ts('aria-labels.pdp.removeFromWishlist')
                : $ts('aria-labels.pdp.addToWishlist')
            "
            @click="emit('update-wishlist')"
          >
            <DefaultIconsHeart
              width="24"
              height="24"
              :fill="addedToWishlist ? 'black' : 'none'"
              aria-hidden="true"
            />
          </button>
          <MoleculesShareButton
            :aria-label="$ts('sharePopup.shareProd')"
            :popup-title="$ts('sharePopup.shareProd')"
            :url="fullURL"
            sharing-icons-pdp
          />
        </div>
      </div>
    </div>

    <!-- PRDOUCT INFO SIDEBARS -->
    <PdpEcommerceBlockInfoAndDetailsSideSlide
      :product-details="productDetails"
      :has-virtual-mirror="hasVirtualMirror"
      @open-virtual-mirror="emit('open-virtual-mirror', 'details_layer')"
    />
    <PdpEcommerceBlockProductCareSideSlide
      v-show="!!productCare.items?.length"
      :product-care="productCare"
      :product-composition="productDetails.composition"
    />
    <PdpEcommerceBlockProductFocusSideSlide
      v-show="!!productFocus?.items?.length"
      :items="productFocus?.items ?? []"
    />
  </div>
</template>
